import { DetailDescriptionDataSource } from '../DetailDescriptionDataSource';
import { DetailTranslationKeys, ErrorTranslationKeys } from '../../../api/models/TranslationKeys';
import { IDetailDescription, QuoteType } from '../detail.types';
import { VodAsset } from './VodAsset';
import { Backend } from '../../../api/Backend';
import { VodStreamRights } from './VodStreamRights';
import { IAssetDetail } from '../../../api/models/AndromeTV.types';
import { AssetType } from '../../../api/models/Shared.types';

export class VodDetailDescriptionDataSource extends DetailDescriptionDataSource {
  private content: VodAsset;
  private streamRights: VodStreamRights;
  constructor(content: VodAsset, contentType: string, backend: Backend, vodStreamRights: VodStreamRights) {
    super(content.id, contentType, backend);
    this.content = content;
    this.streamRights = vodStreamRights;
  }

  /**
   *
   * @returns {Promise<IDetailDescription>}
   */
  override load(): Promise<IDetailDescription> {
    return this._getDetailDescription(this.content);
  }

  /**
   *
   * @param {IAssetDetail} vod
   * @returns {Promise<IDetailDescription>}
   */
  _getDetailDescription(vod: IAssetDetail): Promise<IDetailDescription> {
    const bookmarkPromise = this.getBookmarkPercentage(this.contentType as AssetType, this.contentId);

    return bookmarkPromise.then((bookmarkResult: number | undefined) => {
      const ageIcon = this.getAgeratingIcon(vod.minimumAge);
      return {
        title: vod.title,
        synopsis: vod.longSynopsis ? vod.longSynopsis : vod.shortSynopsis,
        ageRating: ageIcon ? { src: ageIcon } : undefined,
        score: {
          imdb: vod.score,
          operator: vod.operatorScore,
        },
        metaInformation: this.getMetaInformation(
          this.getFormattedSeasonInfo(vod.season, vod.episode),
          undefined,
          undefined
        ),
        ...this.getSubscriptionPurchaseExpireInfo(),
        resolution: this.getResolutionIconFromStreams(vod.streams, true),
        secondaryMetaInfo: this.getSecondaryMetaInfo(vod),
        credits: this.filterCredits(vod.credits),
        parentalControlIcons: this.getParentalIcons(vod.parentalRatings),
        bookmarkPercentage: bookmarkResult,
      } as IDetailDescription;
    });
  }

  getSecondaryMetaInfo(vod: IAssetDetail): string {
    let result = [];
    if (vod.productionYear !== undefined && vod.productionYear > 0) {
      result.push(vod.productionYear.toString());
    }
    if (vod.duration !== undefined && vod.duration > 0) {
      let minutes = vod.duration / 60;
      minutes = minutes > 1 ? minutes : 1;
      minutes = Math.floor(minutes);
      const minStr = this.backend.translations.forKey(DetailTranslationKeys.detail_duration_min);
      result.push(`${minutes} ${minStr}`);
    }
    if (vod.genres) result = result.concat(this.backend.genres.forGenres(vod.genres));
    return result.join(this.Constants.separator);
  }

  /**
   *
   * @returns {{pricing: string, subscription: string, expire: string}}
   */
  getSubscriptionPurchaseExpireInfo(): {
    pricing: string | undefined;
    subscription: string | undefined;
    expire: string | undefined;
  } {
    const result: {
      pricing: string | undefined;
      subscription: string | undefined;
      expire: string | undefined;
    } = {
      pricing: undefined,
      subscription: undefined,
      expire: undefined,
    };
    if (this.streamRights.isWatchableBecauseOfBundle()) {
      const latestBundleQuote = this.streamRights.getLatestValiQuoteForType(QuoteType.BUNDLE);
      const bundle = this.streamRights.getBundle(latestBundleQuote?.entryId);
      if (bundle && bundle.title) {
        result.subscription = this.backend.translations
          .forKey(DetailTranslationKeys.detail_included_in_bundle)
          .replace('$1', bundle.title);
      }

      if (latestBundleQuote) result.expire = this.getAvailableUntilFromQuote(this.backend, latestBundleQuote);
    } else if (this.streamRights.hasWatchableOttTVOD()) {
      const latestTvodQuote = this.streamRights.getLatestValiQuoteForType(QuoteType.TVOD);
      if (latestTvodQuote) {
        result.expire = this.getAvailableUntilFromQuote(this.backend, latestTvodQuote);
      }
    } else if (this.content.isExpired()) {
      result.subscription = this.backend.translations.forKey(ErrorTranslationKeys.error_vod_expired);
      return result;
    } else if (this.streamRights.hasWatchableOttFVOD()) {
      result.subscription = this.backend.translations.forKey(DetailTranslationKeys.detail_free_to_watch);
    } else if (this.streamRights.hasWatchableOttSVOD()) {
      result.subscription = this.backend.translations.forKey(DetailTranslationKeys.detail_included_in_subscriptions);
    } else {
      result.pricing = this.getPricingInfo(this.streamRights.rentableOttStreams.map(str => str.stream));

      if (this.streamRights.hasUpsell()) {
        result.subscription = this.backend.translations.forKey(DetailTranslationKeys.detail_available_in_subscription);
      }

      if (this.streamRights && this.streamRights.upsell && this.streamRights.upsell.image) {
        //TODO image upsell instead of name
        result.subscription += ' ' + this.streamRights.upsell.name;
      } else if (this.streamRights && this.streamRights.upsell && this.streamRights.upsell.name) {
        result.subscription += ' ' + this.streamRights.upsell.name;
      }
    }

    return result;
  }
}
